/**
 * ScopeBar — the page's own scope, declared by the page (19 Aug 2026, Mike).
 *
 * SCOPE IS NOT GLOBAL. It used to live in the top bar, which forced every page to share
 * one set of axes and made the header lie: Capacity has no year and no month/week grain,
 * so a Monthly/Weekly pair sat above it doing nothing, and Margin's Weekly setting asked
 * Sage for a week it does not keep. A page now declares the axes it actually has, and the
 * header carries identity alone.
 *
 * An axis is either a CHOICE (options + value + onChange) or a NODE the page supplies —
 * a date range, or a plain fact like a fixed horizon. A fact is rendered as text, never as
 * a control: a dropdown that cannot drop is the same lie in a smaller place.
 */

import React from 'react'
import clsx from 'clsx'

export interface ScopeOption {
  value: string
  label: string
  /** Renders the option unclickable, with `title` saying why. */
  disabled?: boolean
  title?: string
}

export interface ScopeAxis {
  /** The axis name — 'Entity', 'Grain', 'Period', 'Horizon', 'Lens'. */
  label: string
  options?: readonly ScopeOption[]
  value?: string
  onChange?: (value: string) => void
  /** Arbitrary control for this axis (a date range), or plain text for a fixed fact. */
  node?: React.ReactNode
}

const chip = (active: boolean, disabled = false): string =>
  clsx(
    'cx-btn whitespace-nowrap rounded-lg border px-3 py-1.5 text-[12.5px] transition-colors',
    active ? 'border-brand bg-info-bg font-semibold text-brand' : 'border-line bg-panel font-medium text-mut',
    disabled && 'cursor-not-allowed opacity-40'
  )

const AXIS_LABEL = 'font-mono text-[10px] uppercase tracking-[.14em] text-mut2'

function Choice({ axis }: { axis: ScopeAxis }): React.JSX.Element {
  return (
    <div role="group" aria-label={axis.label} className="flex items-center gap-[5px]">
      {(axis.options ?? []).map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => axis.onChange?.(option.value)}
          disabled={option.disabled}
          aria-pressed={option.value === axis.value}
          title={option.title}
          className={chip(option.value === axis.value, option.disabled)}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

export default function ScopeBar({ axes }: { axes: readonly ScopeAxis[] }): React.JSX.Element {
  return (
    <div
      aria-label="Scope"
      className="mb-4 flex flex-wrap items-center gap-x-5 gap-y-3 rounded-xl border border-dashed border-line px-4 py-3"
    >
      {axes.map((axis) => (
        <div key={axis.label} className="flex items-center gap-2.5">
          <span className={AXIS_LABEL}>{axis.label}</span>
          {axis.node ?? <Choice axis={axis} />}
        </div>
      ))}
    </div>
  )
}
